import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  RoundCreationSettingsResponse,
  RoundCreationSettingsUpdate,
  RoundRebuildResponse,
} from '../../../data-models/round-creation-settings.data-model';
import { getErrorMessage } from '../../../services/queryHelpers';
import { getRoundLabel } from '../../../util/preference-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { useDebouncedValue } from '../../PortfolioOverview/hooks/useDebouncedValue';
import { ConfigStatus } from '../DataDisplay/useDisplayConfigState';
import {
  roundCreationConfigSchema,
  RoundCreationFormValues,
  toSettingsUpdate,
} from './roundCreationConfig.schema';
import { sectionCopy } from './roundCreationCopy';
import { useRoundCreationConfigActions } from './useRoundCreationConfigActions';

export function toFormValues(settings: RoundCreationSettingsResponse): RoundCreationFormValues {
  return {
    roundCreationMatchMode: settings.roundCreationMatchMode,
    roundCreationWindowDays: settings.roundCreationWindowDays,
    transactionAssignmentMatchMode: settings.transactionAssignmentMatchMode,
    transactionAssignmentWindowDays: settings.transactionAssignmentWindowDays,
    unassignedGraceDays: settings.unassignedGraceDays,
    ledgerRole: settings.ledgerRole ?? null,
    docsRole: settings.docsRole,
    precedence: settings.precedence ?? null,
    conversionsCreateRounds: settings.conversionsCreateRounds,
    assignmentMode: settings.assignmentMode,
    securityTypes: settings.securityTypes ?? null,
  };
}

const DRAFT_DEBOUNCE_MS = 500;

export function useRoundCreationConfigFormState() {
  // Whether this page has written anything yet. The chip itself is derived below: "modified" is a
  // fact about the form, so latching it in state left it saying "modified" over a saved page.
  const [hasSaved, setHasSaved] = useState(false);
  const { settings, isLoading, loadError, saveConfig, rebuild, isRebuilding } =
    useRoundCreationConfigActions();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const schema = roundCreationConfigSchema();
  const methods = useForm<RoundCreationFormValues>({
    resolver: yupResolver(schema as ObjectSchema<RoundCreationFormValues>),
    defaultValues: settings ? toFormValues(settings) : undefined,
    mode: 'all',
  });

  const {
    control,
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  // What an unchosen docsRole resolves to. The precedence control is shown against it, so the
  // payload has to be built against it too or a choice made on an inherited CREATE is dropped.
  const resolvedDocsRole = settings?.effective?.docsRole ?? null;

  // Sync the form to the server row whenever its version changes (first load, a save, a rebuild).
  // A write pins the ref itself, so the refetch it triggers does not re-seed the page a second time.
  const syncedVersionRef = useRef<number | null>(null);
  useEffect(() => {
    if (!settings || syncedVersionRef.current === settings.settingsVersion) {
      return;
    }
    syncedVersionRef.current = settings.settingsVersion;
    reset(toFormValues(settings));
  }, [settings, reset]);

  const editStatus: ConfigStatus = isDirty ? 'modified' : hasSaved ? 'saved' : 'initial';

  const onSave = useCallback(async () => {
    const valid = await trigger();
    if (!valid) {
      captureFormValidationError('Round Creation Config', errors);
      return;
    }
    try {
      const saved = await saveConfig(toSettingsUpdate(getValues(), resolvedDocsRole));
      // Re-seed from the server's row: the form stops being dirty (so Save disables again and the
      // status sticks) and shows exactly what was stored, version included.
      syncedVersionRef.current = saved.settingsVersion;
      reset(toFormValues(saved));
      setHasSaved(true);
      pushSuccessToast({ message: sectionCopy(getRoundLabel()).savedGoForward });
    } catch (err) {
      // A 422 names the offending pairing; the form stays modified so nothing is lost.
      pushErrorToast({ message: getErrorMessage(err, 'The settings could not be saved') });
    }
  }, [trigger, errors, saveConfig, getValues, resolvedDocsRole, reset, pushSuccessToast, pushErrorToast]);

  /**
   * The rebuild dialog saves through its own endpoint, so nothing here has re-seeded the form. The
   * values that were sent are the stored ones now: making them the form's defaults clears isDirty,
   * and pinning the version it returned stops the refetch that follows from resetting the page.
   */
  const onApplied = useCallback(
    (response: RoundRebuildResponse) => {
      syncedVersionRef.current = response.settingsVersion;
      reset(getValues());
      setHasSaved(true);
    },
    [getValues, reset]
  );

  // Back to the saved row, discarding the edit. Distinct from Reset, which loads the defaults.
  const onCancel = useCallback(async () => {
    if (!settings) {
      return;
    }
    reset(toFormValues(settings));
    setHasSaved(false);
  }, [reset, settings]);

  // Server-sent defaults, never values kept in this repo. Fills the form; Save persists.
  const onReset = useCallback(async () => {
    if (!settings) {
      return;
    }
    reset(settings.defaults, { keepDefaultValues: true });
  }, [reset, settings]);

  // The draft the preview reads: current values, debounced, only while they validate.
  // Validity is read off the debounced snapshot rather than the live form: the live one turns valid
  // a render before the debounce catches up, and that render sends a draft the API answers 422.
  const watched = useWatch({ control });
  const debounced = useDebouncedValue(watched, DRAFT_DEBOUNCE_MS);
  const draft: RoundCreationSettingsUpdate | null = schema.isValidSync(debounced)
    ? toSettingsUpdate(debounced as RoundCreationFormValues, resolvedDocsRole)
    : null;

  return {
    editStatus,
    methods,
    onSave,
    onReset,
    onCancel,
    settings,
    isLoading,
    loadError,
    draft,
    rebuild,
    isRebuilding,
    onApplied,
  };
}
